// Какво пише във всяка кутия на таблото и какво правят бутоните ѝ. Числата идват
// от обобщенията (`dashboardModel`), думите — от i18n. Бутоните правят СЪЩОТО
// като на началния екран на операцията: при незавършена моя чернова (доставка,
// изписване, инвентаризация, прехвърляне) „Продължи" я отваря (заета → „Поеми
// редакцията"), а „+ Нова" пита „Продължи / Изтрий и започни нова" (модалите
// са на страницата — `DraftHomeModals`). Незавършена инвентаризация (моя ИЛИ на
// колега) обръща кутията „Инвентаризация": кой склад, кой брои, колко е
// преброено, кога — и „Продължи" (моята) / „Поеми" (на колега).
import type { ReactNode } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OPERATIONS_NAV_GROUP } from "../../components/shell/inventoryNav";
import type { HomeDrafts } from "../../components/drafts/useHomeDrafts";
import { DRAFT_TEXTS } from "../../components/drafts/draftTexts";
import { TruncatedText } from "../../components/list/TruncatedText";
import { isMineDraft, unfinishedStocktakeLine } from "../../stocktake/components/home/stocktakeUnfinished";
import {
  DASHBOARD_BOX_SLUGS,
  attentionLevelOf,
  type DashboardBoxSlug,
  type DashboardSummaries,
  type DeliveryDraftsSummary,
  type InterchangeSummary,
  type PaymentsSummary,
  type PostingSummary,
  type ShortageSummary,
  type StocktakeSummary,
  type TodaySummary,
} from "../dashboardModel";
import type { DashboardBoxView } from "./DashboardBox";
import { BoxAlert, BoxAllClear, BoxCount, BoxMoney, BoxNote } from "./DashboardBoxText";
import { BOX_ACTION_CLASS, BOX_MAIN_ACTION_CLASS } from "./dashboardStyles";

// Краткото име в кутията; пълното (от менюто) е в tooltip-а и за екранния четец
const BOX_TITLE_KEY: Record<DashboardBoxSlug, string> = {
  posting: "inventoryPage.dashBoxPosting",
  delivery: "inventoryPage.dashBoxDelivery",
  "delivery-payments": "inventoryPage.dashBoxPayments",
  writeoff: "inventoryPage.dashBoxWriteoff",
  stocktake: "inventoryPage.dashBoxStocktake",
  transfer: "inventoryPage.dashBoxTransfer",
  shortage: "inventoryPage.dashBoxShortage",
  interchangeables: "inventoryPage.dashBoxInterchange",
};

// Черновите на листовете с кутия: моята („Продължи") и „+ Нова" при
// незавършена — въпросът „Продължи / Изтрий" (`startNew`)
export type DashboardDraftType = "delivery" | "writeoff" | "stocktake" | "transfer";
export type DashboardDrafts = Record<DashboardDraftType, Pick<HomeDrafts, "mine" | "startNew">>;

type Options = {
  summaries: DashboardSummaries;
  drafts: DashboardDrafts;
  warehouseId: number | null;
  warehouseName: string | null;
};

// Части на реда под числото, разделени с „ · "
// (нищо за казване → null, не празен ред)
const joinParts = (parts: ReactNode[]): ReactNode => {
  const shown = parts.filter((part) => part !== null && part !== undefined && part !== false && part !== "");
  return shown.length
    ? shown.map((part, index) => (
        <span key={index}>
          {index > 0 && " · "}
          {part}
        </span>
      ))
    : null;
};

export const useDashboardBoxes = ({
  summaries,
  drafts,
  warehouseId,
  warehouseName,
}: Options): DashboardBoxView[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const go = (path: string, state?: unknown) =>
    navigate(path, state === undefined ? undefined : { state });
  // „Вашата чернова: Бар · Изтекъл срок" — моята незавършена в кутията
  const mineLine = (type: DashboardDraftType) => {
    const mine = drafts[type].mine;
    return mine ? t("inventoryPage.dr6DashDraftMine", { label: mine.label || t(DRAFT_TEXTS[type].labelFallback) }) : null;
  };
  // „Продължи" (моята незавършена) пред бутона „+ Нова"
  const withContinue = (type: DashboardDraftType, newButton: ReactNode): ReactNode => {
    const mine = drafts[type].mine;
    return mine ? (
      <>
        <button type="button" onClick={mine.onOpen} className={BOX_MAIN_ACTION_CLASS}>
          {t("inventoryPage.dashContinue")}
        </button>
        {newButton}
      </>
    ) : (
      newButton
    );
  };

  // „Инвентаризация" при незавършени броения: до 2 реда „Бар · Мария · 12
  // преброени · вчера 18:40"; основното действие е за първото — моята
  // („Продължи") или на колега („Поеми"); „+ Нова" остава за най-отдавна броения
  const unfinishedStocktakeContent = (
    summary: StocktakeSummary,
  ): Pick<DashboardBoxView, "headline" | "detail" | "actions"> => {
    const unfinished = summary.unfinished;
    const mineDraft = drafts.stocktake.mine;
    const first = unfinished[0];
    const shown = unfinished.slice(0, 2);
    const stalest = summary.stalest;
    const newState = stalest ? { warehouseId: stalest.warehouseId } : undefined;
    const newLabel = stalest
      ? t("inventoryPage.dashNewStocktakeFor", { warehouse: stalest.warehouseName })
      : t("inventoryPage.newStocktakePlus");
    return {
      headline: (
        <BoxCount
          value={unfinished.length}
          label={t("inventoryPage.dashStkUnfinished", { count: unfinished.length })}
        />
      ),
      detail: (
        <span className="flex min-w-0 flex-col">
          {shown.map((draft) => {
            const line = unfinishedStocktakeLine(t, draft, {
              mine: isMineDraft(draft, mineDraft),
              withWarehouse: true,
            });
            return <TruncatedText key={draft.key} text={line} className="text-ink-2" />;
          })}
          {unfinished.length > shown.length && (
            <span>{t("inventoryPage.dashStkMore", { count: unfinished.length - shown.length })}</span>
          )}
        </span>
      ),
      actions: (
        <>
          <button type="button" onClick={first.onOpen} className={BOX_MAIN_ACTION_CLASS}>
            {isMineDraft(first, mineDraft) ? t("inventoryPage.dashContinue") : t("inventoryPage.dashTakeOver")}
          </button>
          <button
            type="button"
            onClick={() =>
              mineDraft ? drafts.stocktake.startNew(newState) : go("/inventory/stocktake/new", newState)
            }
            aria-label={newLabel}
            title={newLabel}
            className={BOX_ACTION_CLASS}
          >
            {t("inventoryPage.dashNewFem")}
          </button>
        </>
      ),
    };
  };

  const content = (slug: DashboardBoxSlug): Pick<DashboardBoxView, "headline" | "detail" | "actions"> => {
    const box = summaries[slug];
    if (box.state !== "ready") return {};
    switch (slug) {
      case "posting": {
        const summary = box.value as PostingSummary;
        if (!summary.pending || !summary.oldest) {
          return { headline: <BoxAllClear text={t("inventoryPage.dashPostingNone")} /> };
        }
        const oldest = summary.oldest;
        return {
          headline: (
            <BoxCount
              value={summary.pending}
              label={t("inventoryPage.dashPendingLabel", { count: summary.pending })}
            />
          ),
          detail: joinParts([
            summary.overdue > 0 && <BoxAlert>{t("inventoryPage.dashOverdue", { count: summary.overdue })}</BoxAlert>,
            t("inventoryPage.dashOldestReport", {
              number: oldest.reportNumber,
              date: dayjs(oldest.reportDate).format("DD.MM"),
            }),
          ]),
          actions: (
            <button
              type="button"
              onClick={() => go("/inventory/posting", { selectReportId: oldest.id })}
              className={BOX_MAIN_ACTION_CLASS}
            >
              {t("inventoryPage.dashPostOldest")}
            </button>
          ),
        };
      }
      case "delivery": {
        const summary = box.value as DeliveryDraftsSummary;
        const mineDraft = drafts.delivery.mine;
        const newButton = (
          <button
            key="new"
            type="button"
            onClick={() => (mineDraft ? drafts.delivery.startNew() : go("/inventory/delivery/new"))}
            aria-label={t("inventoryPage.newDeliveryPlus")}
            title={t("inventoryPage.newDeliveryPlus")}
            className={mineDraft ? BOX_ACTION_CLASS : BOX_MAIN_ACTION_CLASS}
          >
            {t("inventoryPage.dashNewFem")}
          </button>
        );
        return {
          headline: summary.total ? (
            <BoxCount
              value={summary.total}
              label={t("inventoryPage.dashDraftsLabel", { count: summary.total })}
            />
          ) : (
            <BoxAllClear text={t("inventoryPage.dashDraftsNone")} />
          ),
          detail: joinParts([
            summary.held > 0 && <BoxAlert>{t("inventoryPage.dashDraftHeld", { count: summary.held })}</BoxAlert>,
            summary.mine &&
              t("inventoryPage.dashDraftMine", {
                supplier: summary.mine.label || t("inventoryPage.homeNoSupplier"),
              }),
            summary.mine?.total != null && <BoxMoney value={summary.mine.total} />,
            summary.colleagues.length > 0 &&
              t("inventoryPage.dashDraftColleagues", { names: summary.colleagues.join(", ") }),
          ]),
          actions: withContinue("delivery", newButton),
        };
      }
      case "delivery-payments": {
        const summary = box.value as PaymentsSummary;
        if (!summary.count) {
          return { headline: <BoxAllClear text={t("inventoryPage.dashAllPaid")} /> };
        }
        return {
          headline: (
            <BoxCount
              value={summary.count}
              label={t("inventoryPage.dashUnpaidLabel", { count: summary.count })}
            />
          ),
          detail: joinParts([
            summary.overdue > 0 && (
              <BoxAlert>{t("inventoryPage.dashPayOverdue", { count: summary.overdue })}</BoxAlert>
            ),
            <BoxMoney value={summary.total} />,
            summary.oldestDays != null &&
              summary.oldestDays > 0 &&
              t("inventoryPage.dashOldestWaits", { count: summary.oldestDays }),
          ]),
          actions: (
            <button
              type="button"
              onClick={() => go("/inventory/delivery-payments")}
              className={BOX_MAIN_ACTION_CLASS}
            >
              {t("inventoryPage.dashPay")}
            </button>
          ),
        };
      }
      case "writeoff":
      case "transfer": {
        const summary = box.value as TodaySummary;
        const isWriteoff = slug === "writeoff";
        const where = isWriteoff ? warehouseName : t("inventoryPage.dashAllWarehouses");
        const hasMine = drafts[slug].mine != null;
        const newLabel = t(isWriteoff ? "inventoryPage.newWriteoffPlus" : "inventoryPage.newTransferPlus");
        const newButton = (
          <button
            key="new"
            type="button"
            // Без склад няма откъде да се изписва (както на началния екран)
            disabled={isWriteoff && warehouseId == null}
            onClick={() =>
              hasMine
                ? drafts[slug].startNew()
                : go(isWriteoff ? "/inventory/writeoff/new" : "/inventory/transfer/new")
            }
            aria-label={newLabel}
            title={newLabel}
            className={hasMine ? BOX_ACTION_CLASS : BOX_MAIN_ACTION_CLASS}
          >
            {t("inventoryPage.dashNewNeut")}
          </button>
        );
        return {
          headline: summary.count ? (
            <BoxCount
              value={summary.count}
              label={t(
                isWriteoff ? "inventoryPage.dashWriteoffsToday" : "inventoryPage.dashTransfersToday",
                { count: summary.count },
              )}
            />
          ) : (
            <BoxNote
              text={t(isWriteoff ? "inventoryPage.dashWriteoffsNone" : "inventoryPage.dashTransfersNone")}
            />
          ),
          detail: joinParts([
            mineLine(slug),
            summary.count > 0 && summary.total != null && <BoxMoney value={summary.total} />,
            where,
          ]),
          actions: withContinue(slug, newButton),
        };
      }
      case "stocktake": {
        const summary = box.value as StocktakeSummary;
        const stalest = summary.stalest;
        if (summary.unfinished.length) return unfinishedStocktakeContent(summary);
        if (!stalest) return { headline: <BoxNote text={t("inventoryPage.dashNoWarehouses")} /> };
        const newLabel = t("inventoryPage.dashNewStocktakeFor", { warehouse: stalest.warehouseName });
        const hasMine = drafts.stocktake.mine != null;
        const newState = { warehouseId: stalest.warehouseId };
        // Новата инвентаризация е за склада, за който кутията говори
        const newButton = (
          <button
            key="new"
            type="button"
            onClick={() =>
              hasMine ? drafts.stocktake.startNew(newState) : go("/inventory/stocktake/new", newState)
            }
            aria-label={newLabel}
            title={newLabel}
            className={hasMine ? BOX_ACTION_CLASS : BOX_MAIN_ACTION_CLASS}
          >
            {t("inventoryPage.dashNewFem")}
          </button>
        );
        return {
          headline:
            stalest.ageDays == null ? (
              <BoxNote text={t("inventoryPage.dashNeverCounted", { warehouse: stalest.warehouseName })} />
            ) : stalest.ageDays === 0 ? (
              <BoxAllClear
                text={t("inventoryPage.dashCountedToday", { count: summary.warehouses })}
              />
            ) : (
              <BoxCount
                value={stalest.ageDays}
                label={t("inventoryPage.dashDaysSinceCount", { count: stalest.ageDays })}
              />
            ),
          // „днес" и „не е броен" вече казват всичко; иначе — кой склад и защо той
          detail: joinParts([
            mineLine("stocktake"),
            stalest.ageDays == null || stalest.ageDays === 0
              ? null
              : summary.warehouses > 1
                ? t("inventoryPage.dashStalestOf", {
                    warehouse: stalest.warehouseName,
                    total: summary.warehouses,
                  })
                : stalest.warehouseName,
          ]),
          actions: withContinue("stocktake", newButton),
        };
      }
      case "shortage": {
        const summary = box.value as ShortageSummary;
        if (!summary.products) {
          return { headline: <BoxAllClear text={t("inventoryPage.dashShortageNone")} /> };
        }
        return {
          headline: (
            <BoxCount
              value={summary.products}
              label={t("inventoryPage.dashShortageLabel", { count: summary.products })}
            />
          ),
          detail: joinParts([
            <BoxMoney value={summary.gross} />,
            summary.warehouses > 1 &&
              t("inventoryPage.dashInWarehouses", { total: summary.warehouses }),
          ]),
        };
      }
      case "interchangeables": {
        const summary = box.value as InterchangeSummary;
        return summary.rules
          ? {
              headline: (
                <BoxCount
                  value={summary.rules}
                  label={t("inventoryPage.dashRulesLabel", { count: summary.rules })}
                />
              ),
              detail: t("inventoryPage.dashRulesHint"),
            }
          : { headline: <BoxNote text={t("inventoryPage.dashRulesNone")} /> };
      }
    }
  };

  return DASHBOARD_BOX_SLUGS.map((slug) => {
    const item = OPERATIONS_NAV_GROUP.items.find((navItem) => navItem.slug === slug)!;
    return {
      slug,
      title: t(BOX_TITLE_KEY[slug]),
      label: t(item.labelKey),
      Icon: item.Icon,
      state: summaries[slug].state,
      signal: attentionLevelOf(slug, summaries),
      onOpen: () => go(`/inventory/${slug}`),
      ...content(slug),
    };
  });
};
